import React, { useEffect, useState } from 'react';
import { App, Button, Form, Input, Modal, Select, Table, Tag, message } from 'antd';
import type { TableColumnsType } from 'antd';
import { SortAscendingOutlined } from '@ant-design/icons';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { pickPageName, pickPortalName } from '../../../../utils/dataPortalCache';
import GroupReorderModal from './GroupReorderModal';
import {
    CREATE_DATA_PORTAL_GROUP,
    DELETE_DATA_PORTAL_GROUP,
    DataPortalGroupType,
    REORDER_DATA_PORTAL_GROUPS,
    UPDATE_DATA_PORTAL_GROUP,
} from '../graphql/queries';

interface Props {
    groups: DataPortalGroupType[];
    /** 未分组的看板数，末行只读展示 */
    ungroupedCount: number;
    /** 可选来源门户，已按 sortOrder 排好序；新建分组时选一个，之后不可改 */
    portalCodes: string[];
    onChanged: () => void;
}

/**
 * 分组视图。
 *
 * 顺序用 ↑↓ 调，但**提交的是整个顺序列表**而不是「与相邻项互换数值」——
 * 存量分组的 sortOrder 全是 0，互换数值根本换不动。
 *
 * 末行固定「未分组」：它是虚拟分组（看板的 group_id 为 NULL），不在分组表占行，
 * 所以不可重命名/删除，看板数只读。
 */
const GroupTable: React.FC<Props> = ({ groups, ungroupedCount, portalCodes, onChanged }) => {
    const { t, i18n } = useTranslation();
    const { modal } = App.useApp();
    const [form] = Form.useForm();
    const [editing, setEditing] = useState<DataPortalGroupType | null>(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [reorderOpen, setReorderOpen] = useState(false);

    const [createGroup] = useMutation(CREATE_DATA_PORTAL_GROUP);
    const [updateGroup] = useMutation(UPDATE_DATA_PORTAL_GROUP);
    const [deleteGroup] = useMutation(DELETE_DATA_PORTAL_GROUP);
    const [reorderGroups] = useMutation(REORDER_DATA_PORTAL_GROUPS);

    const openCreate = () => {
        setEditing(null);
        setModalOpen(true);
    };

    const openEdit = (group: DataPortalGroupType) => {
        setEditing(group);
        setModalOpen(true);
    };

    // 设值放在 Modal 打开后（Form 已挂载）执行，而不是打开前 —— 否则会触发
    // 「Instance created by useForm is not connected to any Form element」控制台报错。
    // Modal 用 destroyOnClose，每次打开 Form 都是新挂载的，这里据 editing 填充或清空。
    useEffect(() => {
        if (!modalOpen) return;
        if (editing) {
            form.setFieldsValue({
                nameZh: editing.nameZh,
                nameEn: editing.nameEn,
                nameEs: editing.nameEs,
                portalCode: editing.portalCode,
            });
        } else {
            form.resetFields();
            // 新建默认选第一个门户（后端已按 sortOrder 排序，即页面上第一段）
            form.setFieldsValue({ portalCode: portalCodes[0] });
        }
    }, [modalOpen, editing, form, portalCodes]);

    const submit = async () => {
        // 先校验，校验不过不弹二级确认
        const values = await form.validateFields();
        // 三语名 + sortOrder 四个字段都要带上：GraphQL Input 里它们虽有默认值，
        // 但 graphql-kotlin 生成的 schema 仍把它们标为必填，漏传会被拒（missing required fields）
        const input = {
            nameZh: values.nameZh ?? '',
            nameEn: values.nameEn ?? '',
            nameEs: values.nameEs ?? '',
            sortOrder: editing?.sortOrder ?? 0,
            // 编辑时传 null＝不改门户（后端契约，见 DataPortalGroupInput.portalCode）。
            // 不回传现值是因为「回传」和「省略」在后端是两条不同的路径，省略那条更简单也更难写错
            portalCode: editing ? null : values.portalCode,
        };
        // 二级确认：提示改动会立即生效
        modal.confirm({
            title: t('central.dataPortalConfig.confirmDialog.title'),
            content: t('central.dataPortalConfig.confirmDialog.group'),
            okText: t('components.button.confirm') as string,
            cancelText: t('components.button.cancel') as string,
            onOk: async () => {
                try {
                    if (editing) {
                        await updateGroup({ variables: { input: { ...input, id: editing.id } } });
                    } else {
                        await createGroup({ variables: { input } });
                    }
                    message.success(t('central.dataPortalConfig.messages.saved') as string);
                    setModalOpen(false);
                    onChanged();
                } catch (e: any) {
                    message.error(e.message);
                }
            },
        });
    };

    const handleDelete = (group: DataPortalGroupType) => {
        modal.confirm({
            title: t('central.dataPortalConfig.messages.deleteGroupConfirm'),
            // 删组不丢看板，所以不要求输入组名二次确认（删看板才要求）
            content: t('central.dataPortalConfig.messages.deleteGroupTip'),
            okText: t('components.button.confirm') as string,
            cancelText: t('components.button.cancel') as string,
            okButtonProps: { danger: true },
            onOk: async () => {
                try {
                    await deleteGroup({ variables: { id: group.id } });
                    message.success(t('central.dataPortalConfig.messages.deleted') as string);
                    onChanged();
                } catch (e: any) {
                    message.error(e.message);
                }
            },
        });
    };

    // 批量拖拽排序：弹窗里拖到位，一次把整串顺序交给后端整序，避免几十个分组一个个 ↑↓
    const handleReorder = async (orderedIds: string[]) => {
        try {
            await reorderGroups({ variables: { ids: orderedIds } });
            message.success(t('central.dataPortalConfig.messages.saved') as string);
            setReorderOpen(false);
            onChanged();
        } catch (e: any) {
            message.error(e.message);
        }
    };

    type Row = DataPortalGroupType & { isSystem?: boolean };
    const rows: Row[] = [
        ...groups,
        {
            id: '__ungrouped__',
            portalCode: '',
            nameEn: '',
            nameZh: '',
            nameEs: '',
            sortOrder: -1,
            enabledPageCount: ungroupedCount,
            isSystem: true,
        },
    ];

    const columns: TableColumnsType<Row> = [
        {
            title: t('central.dataPortalConfig.fields.groupName'),
            key: 'name',
            // 排序不再用行内 ↑↓（不方便），统一走右上角「调整排序」拖拽弹窗。
            // 表格行序本身即当前展示顺序（sort_order 降序）。「未分组」是系统分组，行首挂标记。
            render: (_: unknown, record) =>
                record.isSystem ? (
                    <span className="flex items-center gap-2">
                        <Tag>{t('central.dataPortalConfig.systemGroup')}</Tag>
                        {t('pages.dataPortal.ungrouped')}
                    </span>
                ) : (
                    pickPageName(record, i18n.language)
                ),
        },
        {
            title: t('central.dataPortalConfig.fields.portalCode'),
            key: 'portalCode',
            width: 140,
            // 「未分组」是虚拟分组，不属于任何单一门户（每个门户各有一段），所以不展示门户
            render: (_: unknown, record) =>
                record.isSystem ? (
                    <span className="text-gray-400">-</span>
                ) : (
                    pickPortalName(record.portalCode, t)
                ),
        },
        {
            title: t('central.dataPortalConfig.fields.groupPageCount'),
            dataIndex: 'enabledPageCount',
            key: 'enabledPageCount',
            width: 120,
        },
        {
            title: t('common.action'),
            key: 'action',
            width: 180,
            render: (_: unknown, record) =>
                record.isSystem ? (
                    <span className="text-gray-400">-</span>
                ) : (
                    <div className="flex gap-2">
                        <Button type="link" style={{ paddingLeft: 0 }} onClick={() => openEdit(record)}>
                            {t('central.dataPortalConfig.actions.rename')}
                        </Button>
                        <Button type="link" danger onClick={() => handleDelete(record)}>
                            {t('components.button.delete')}
                        </Button>
                    </div>
                ),
        },
    ];

    return (
        <div>
            <div className="flex justify-end gap-2 mb-3">
                {/* 分组 ≥2 个才给拖拽排序入口，1 个没得排 */}
                {groups.length > 1 && (
                    <Button icon={<SortAscendingOutlined />} onClick={() => setReorderOpen(true)}>
                        {t('central.dataPortalConfig.actions.reorderGroups')}
                    </Button>
                )}
                <Button type="primary" onClick={openCreate}>
                    {t('central.dataPortalConfig.actions.newGroup')}
                </Button>
            </div>
            <Table size="middle" rowKey="id" columns={columns} dataSource={rows} pagination={false} />
            <div className="mt-2 text-xs text-gray-400">{t('central.dataPortalConfig.messages.deleteGroupTip')}</div>

            <Modal
                open={modalOpen}
                title={
                    editing
                        ? t('central.dataPortalConfig.actions.rename')
                        : t('central.dataPortalConfig.actions.newGroup')
                }
                onCancel={() => setModalOpen(false)}
                onOk={submit}
                okText={t('components.button.confirm') as string}
                cancelText={t('components.button.cancel') as string}
                destroyOnClose
            >
                <Form form={form} layout="vertical">
                    {/* 门户归属创建后不可改：改了会让组内全部看板与分组错配，后端也会拒绝。
                        编辑态禁用而不是隐藏 —— 管理员要能看到这个组属于哪个门户 */}
                    <Form.Item
                        name="portalCode"
                        label={t('central.dataPortalConfig.fields.portalCode')}
                        tooltip={t('central.dataPortalConfig.tips.groupPortalImmutable')}
                        rules={[{ required: true }]}
                    >
                        <Select
                            disabled={editing != null}
                            options={portalCodes.map((code) => ({
                                value: code,
                                label: pickPortalName(code, t),
                            }))}
                        />
                    </Form.Item>
                    {/* 三语至少填一个，与看板名同一套规则；校验挂在中文那项上，只出一条错误提示 */}
                    <Form.Item
                        name="nameZh"
                        label={t('central.dataPortalConfig.fields.nameZh')}
                        dependencies={['nameEn', 'nameEs']}
                        rules={[
                            {
                                validator: async () => {
                                    const { nameEn, nameZh, nameEs } = form.getFieldsValue(['nameEn', 'nameZh', 'nameEs']);
                                    if (!nameEn?.trim() && !nameZh?.trim() && !nameEs?.trim()) {
                                        throw new Error(t('central.dataPortalConfig.messages.nameAtLeastOne') as string);
                                    }
                                },
                            },
                        ]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item name="nameEn" label={t('central.dataPortalConfig.fields.nameEn')}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="nameEs" label={t('central.dataPortalConfig.fields.nameEs')}>
                        <Input />
                    </Form.Item>
                </Form>
            </Modal>

            <GroupReorderModal
                open={reorderOpen}
                groups={groups}
                portalCodes={portalCodes}
                onCancel={() => setReorderOpen(false)}
                onConfirm={handleReorder}
            />
        </div>
    );
};

export default GroupTable;
